import { EvaEndpoint } from '../endpoint.js';
import { EvaContextEndpointStatus } from './endpoint-status.js';
import { EvaContextUser } from './user.js';
/**
 * An EVA context is the combination of:
 * - endpoint url
 * - endpoint role (CLOUD, WATCHTOWER, etc)
 * - endpoint reachability status
 * - an organization unit ID (optional)
 * - a current user (optional)
 *
 * Functionally it provides
 * - endpoint reachability checking (including slow indicator)
 * - user login status
 * - callService helper
 * - Automatic JWT token refresh when using callService helper
 * - Manual JWT token refresh and expiration time check
 *
 * You can disable the endpoint reachability check by setting the reachabilityCheckInterval to 0
 * This will check the reachability once during startup but not afterwards
 */
export class EvaContext {
    /**
     * The EVA endpoint and its current status
     */
    _endpoint;
    get endpoint() { return this._endpoint; }
    endpointStatus;
    /**
     * The current organization unit (if any)
     */
    _organizationUnitId;
    get organizationUnitId() { return this._organizationUnitId; }
    /**
     * The current user (if any)
     */
    user;
    constructor({ applicationToken, authenticationToken, endpointUrl, organizationUnitId, reachabilityCheckInterval, refreshToken, role = 'CLOUD', slowThresholdMilliseconds, validateUserTokenInterval, }) {
        this._endpoint = new EvaEndpoint(endpointUrl);
        this.endpointStatus = new EvaContextEndpointStatus({
            endpoint: this._endpoint,
            reachabilityCheckInterval,
            role,
            slowThresholdMilliseconds,
            status: 'REACHABLE',
            bootstrapTimeout: 10000,
        });
        this._organizationUnitId = organizationUnitId;
        this.user = new EvaContextUser({
            applicationToken,
            authenticationToken,
            endpoint: this._endpoint,
            refreshToken,
            validateUserTokenInterval,
        });
    }
    async connect(params) {
        // Beware that bootstrapping can fail on offline startups
        //
        try {
            await this._endpoint.bootstrap({ timeout: 10000, ...params });
        }
        catch {
            // NOOP
        }
        this.endpointStatus.start();
        this.user.start();
    }
    disconnect() {
        this.endpointStatus.stop();
        this.user.stop();
    }
    /**
     * Helper method to call a service on the endpoint of this context which will automatically set:
     * - organization unit id
     * - user token
     *
     * The contextCallOptions can be used to override this behavior.
     * Values provided for the IEvaServiceCallOptions take priority as well.
     *
     * If the user token is a JWT token it will be refreshed if needed
     */
    async callService(serviceDefinition, payload, options, contextCallOptions) {
        // We should check if the JWT token is still valid before making the call.
        //
        try {
            await this.user.checkAndRefreshJwtToken();
        }
        catch (error) {
            // Let the call continue even when RefreshToken fails
        }
        const authenticationToken = contextCallOptions?.omitAuthenticationToken !== true ? this.user.authenticationToken : undefined;
        try {
            const response = await this._endpoint.callService(serviceDefinition, payload, {
                authenticationToken,
                requestedOrganizationUnitID: contextCallOptions?.omitOrganizationUnitId !== true ? this._organizationUnitId : undefined,
                ...options
            });
            return response;
        }
        catch (error) {
            const httpStatus = error?.response?.status;
            if (httpStatus === 401 && authenticationToken) {
                this.user.expireToken(authenticationToken);
            }
            throw error;
        }
    }
}
//# sourceMappingURL=index.js.map